import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 } from "uuid";

import { sensitiveEnv } from "../config/sensitiveEnv";
import { useAuth } from "../lib/auth/AuthContext";

export interface LDContextValue {
  kind: string;
  key: string;
  email?: string;
  plan_name?: string;
  anonymous?: boolean;
}

interface LDEvalFlagDetail {
  flagVersion: number;
  trackEvents: boolean;
  value: boolean;
  variation: number;
  version: number;
}

export interface LDFlagValues extends Record<string, boolean> {}

export interface LDFlagsContextValue {
  ldFlags: LDFlagValues;
  updateFlagWithContext: (context: LDContextValue) => Promise<LDFlagValues>;
}

interface UseLDConfigResult {
  isFlagsLoading: boolean;
}

const defaultLDFlagsContextValue: LDFlagsContextValue = {
  ldFlags: {},
  updateFlagWithContext: async () => ({}),
};

const LaunchDarklyClientId = sensitiveEnv.launchDarklyClientId;
const LD_EVAL_BASE_URL = "https://app.launchdarkly.com";

/**
 * Converts a base64-encoded string into a URL-safe variant expected by LaunchDarkly.
 *
 * @param value Base64-encoded string value.
 * @returns Base64 URL-safe string.
 */
function base64URLEncode(value: string): string {
  return value.replaceAll("=", "").replaceAll("+", "-").replaceAll("/", "_");
}

/**
 * Serializes and encodes a LaunchDarkly context for use in the evaluation API URL.
 *
 * @param context LaunchDarkly context payload.
 * @returns URL-safe encoded context string.
 */
const encodeContext = (context: LDContextValue): string =>
  base64URLEncode(btoa(JSON.stringify(context)));

/**
 * Fetches evaluated LaunchDarkly flags for the supplied context.
 *
 * @param context LaunchDarkly context used for evaluation.
 * @returns Resolved flag values keyed by camel-cased flag name.
 */
const fetchLDFlagsByContext = async (
  context: LDContextValue
): Promise<LDFlagValues> => {
  try {
    const encodedContext = encodeContext(context);
    const url = `${LD_EVAL_BASE_URL}/sdk/evalx/${LaunchDarklyClientId}/contexts/${encodedContext}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error(
        `LaunchDarkly evaluation API responded with ${response.status}: ${response.statusText}`
      );
    }

    const data: Record<string, LDEvalFlagDetail> = await response.json();
    const camelCaseValue = (name: string): string =>
      name.replaceAll(/-([a-z])/g, (_, char) => char.toUpperCase());

    return Object.fromEntries(
      Object.entries(data).map(([key, detail]) => [
        camelCaseValue(key),
        detail.value,
      ])
    );
  } catch (error) {
    console.error(
      "fetchLDFlagsByContext: failed to fetch flags for context",
      context,
      error
    );
    return {};
  }
};

export const LDFlagsContext = createContext<LDFlagsContextValue>(
  defaultLDFlagsContextValue
);

/**
 * Provides LaunchDarkly flag values and context-driven refresh behavior to descendants.
 *
 * @param children React nodes that consume LaunchDarkly flags.
 * @returns The provider wrapping its children with LaunchDarkly state.
 */
export const LDFlagsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [ldFlags, setLdFlags] = useState<LDFlagValues>({});

  const updateFlagWithContext = useCallback(
    async (context: LDContextValue): Promise<LDFlagValues> => {
      const flags = await fetchLDFlagsByContext(context);
      setLdFlags(flags);
      return flags;
    },
    [setLdFlags]
  );

  const value = useMemo<LDFlagsContextValue>(
    () => ({ ldFlags, updateFlagWithContext }),
    [ldFlags, updateFlagWithContext]
  );

  return (
    <LDFlagsContext.Provider value={value}>{children}</LDFlagsContext.Provider>
  );
};

/**
 * Returns the LaunchDarkly flags context for the current component tree.
 *
 * @returns The current LaunchDarkly flags state and updater function.
 */
export const useLDFlags = (): LDFlagsContextValue => useContext(LDFlagsContext);

/**
 * Returns the current LaunchDarkly feature flag values.
 *
 * @returns Feature flag map from the LaunchDarkly context.
 */
export const useFeatureFlags = (): LDFlagsContextValue["ldFlags"] => {
  const { ldFlags } = useLDFlags();
  return ldFlags;
};

/**
 * Identifies the current user for LaunchDarkly and keeps flag values in sync.
 *
 * @returns Loading state for the current flag refresh cycle.
 */
export const useLDConfig = (): UseLDConfigResult => {
  const { user } = useAuth();
  const { id: userId, email } = user ?? {};
  const { updateFlagWithContext } = useLDFlags();
  const [isFlagsLoading, setIsFlagsLoading] = useState(true);
  const lastIdentifiedContext = useRef<string>("");

  useEffect(() => {
    const updateLdClient = async (): Promise<void> => {
      try {
        if (userId && email) {
          const isSameContext = lastIdentifiedContext.current === email;

          if (isSameContext) {
            return;
          }

          const context: LDContextValue = {
            kind: "user",
            key: userId,
            email,
          };

          setIsFlagsLoading(true);
          await updateFlagWithContext(context);
          lastIdentifiedContext.current = email;
          setIsFlagsLoading(false);
          return;
        }

        setIsFlagsLoading(true);
        await updateFlagWithContext({ kind: "user", key: v4(), email });
        lastIdentifiedContext.current = "";
        setIsFlagsLoading(false);
      } catch (error) {
        console.error("Error updating LaunchDarkly context:", error);
        setIsFlagsLoading(false);
      }
    };

    void updateLdClient();
  }, [userId, email, updateFlagWithContext]);

  return { isFlagsLoading };
};
